import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Heart, Package, Mail, Facebook, Instagram, ShoppingBag, Star, ChevronRight, Send, MessageCircle, Shield, CreditCard, Lock, Users, X, Youtube, Menu, Search, Eye, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { categories, productTypes, featuredProducts, testimonials } from '../data/mock';
import ProductSearch from '../components/ProductSearch';
import { 
  QuickViewModal, 
  RecentlyViewed, 
  StickyShopBar, 
  SocialProofNotification,
  LowStockBadge 
} from '../components/ConversionFeatures';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [email, setEmail] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeError, setSubscribeError] = useState('');
  const [showBanner, setShowBanner] = useState(true);
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [exitEmailSubmitted, setExitEmailSubmitted] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // New conversion feature states
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [liveProducts, setLiveProducts] = useState([]);
  
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactError, setContactError] = useState('');

  // Fetch live products from Shopify for recommendations
  useEffect(() => {
    const fetchLiveProducts = async () => {
      try {
        const response = await axios.get(`${API}/shopify/products?limit=50`);
        if (response.data.products) {
          setLiveProducts(response.data.products);
        }
      } catch (error) {
        console.log('Using fallback products');
        setLiveProducts(featuredProducts);
      }
    };
    fetchLiveProducts();
  }, []);

  // Show sticky bar on scroll
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyBar(window.scrollY > 800);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track page view
  useEffect(() => {
    axios.post(`${API}/analytics/track?event_type=page_view&page=home`).catch(() => {});
  }, []);

  // Smooth scroll for anchor links
  const handleAnchorClick = (e, targetId) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    const element = document.querySelector(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Exit intent detection
  React.useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !exitEmailSubmitted && !localStorage.getItem('exitPopupShown')) {
        setShowExitPopup(true);
        localStorage.setItem('exitPopupShown', 'true');
      }
    };

    // For mobile - detect when user tries to go back/leave
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && !exitEmailSubmitted && !localStorage.getItem('exitPopupShown')) {
        setTimeout(() => {
          if (document.visibilityState === 'hidden') {
            setShowExitPopup(true);
            localStorage.setItem('exitPopupShown', 'true');
          }
        }, 2000);
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [exitEmailSubmitted]);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setSubscribing(true);
    setSubscribeError('');
    
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email });
      setSubscribed(true);
      setEmail('');
      setTimeout(() => {
        setSubscribed(false);
      }, 5000);
    } catch (error) {
      if (error.response?.data?.detail) {
        setSubscribeError(error.response.data.detail);
      } else {
        setSubscribeError('Failed to subscribe. Please try again.');
      }
    } finally {
      setSubscribing(false);
    }
  };

  const handleExitPopupSubmit = async (e) => {
    e.preventDefault();
    setSubscribing(true);
    
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setExitEmailSubmitted(true);
      setEmail('');
    } catch (error) {
      console.error('Exit popup subscription error:', error);
    } finally {
      setSubscribing(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSubmitting(true);
    setContactError('');
    
    try {
      const response = await axios.post(`${API}/contact`, contactForm);
      setContactSubmitted(true);
      setContactForm({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => {
        setContactSubmitted(false);
      }, 5000);
    } catch (error) {
      if (error.response?.data?.detail) {
        setContactError(error.response.data.detail);
      } else {
        setContactError('Failed to send message. Please try again.');
      }
    } finally {
      setContactSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-teal-50">
      {/* Exit Intent Popup - #2 */}
      {showExitPopup && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 relative shadow-2xl animate-in zoom-in duration-300">
            <button 
              onClick={() => setShowExitPopup(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
            
            {!exitEmailSubmitted ? (
              <>
                <div className="text-center mb-6">
                  <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_be17761e-04ff-4edf-8e1f-841eccc40e55/artifacts/she6yj1e_IMG_6151.png" 
                      alt="Charming Creations Logo" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-2">Wait! Don't Go Yet!</h3>
                  <p className="text-xl text-amber-600 font-bold mb-2">Get 15% OFF Your First Order</p>
                  <p className="text-gray-600">Plus exclusive access to new collections</p>
                </div>
                
                <form onSubmit={handleExitPopupSubmit} className="space-y-4">
                  <Input 
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="text-lg py-6 border-2 border-amber-300"
                    required
                  />
                  <Button 
                    type="submit"
                    className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-lg py-6"
                    disabled={subscribing}
                  >
                    {subscribing ? 'Sending...' : 'Send Me My 15% OFF Code'}
                  </Button>
                  <p className="text-xs text-gray-500 text-center">*Code will be sent to your email instantly</p>
                </form>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-10 h-10 text-teal-600 fill-teal-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Check Your Email!</h3>
                <p className="text-gray-600 mb-4">Your 15% OFF code is on its way</p>
                <Button 
                  onClick={() => {
                    setShowExitPopup(false);
                    window.location.href = '/search';
                  }}
                  className="bg-gradient-to-r from-teal-500 to-teal-600 text-white"
                >
                  Start Shopping Now
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* WhatsApp Chat Button - #3 - Sticky for Mobile */}
      <a
        href="https://wa.me/61415145952?text=Hi%2C%20I%27m%20interested%20in%20your%20handcrafted%20resin%20art%20pieces!%20Can%20you%20help%20me%3F"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-24 sm:bottom-20 right-4 z-50 bg-green-500 hover:bg-green-600 text-white rounded-full p-3 sm:p-4 shadow-2xl transition-all hover:scale-110 flex items-center gap-2 group"
      >
        <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
        <span className="hidden md:inline-block text-sm font-semibold whitespace-nowrap ml-1">Chat with us</span>
      </a>
      
      {/* Free Shipping Banner - #1 */}
      {showBanner && (
        <div className="bg-gradient-to-r from-amber-600 via-red-500 to-teal-600 text-white py-2 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer"></div>
          <div className="container mx-auto flex items-center justify-center pr-8 relative">
            <Package className="w-4 h-4 mr-2 flex-shrink-0 animate-bounce" />
            <p className="text-xs sm:text-sm font-bold text-center">
              <span className="sm:hidden">🎁 FREE SHIPPING + 10% OFF: WELCOME10</span>
              <span className="hidden sm:inline">🎁 FREE SHIPPING Australia-wide • Use code WELCOME10 for 10% OFF your first order! 🎁</span>
            </p>
            <button 
              onClick={() => setShowBanner(false)}
              className="absolute right-2 sm:right-4 hover:opacity-70 transition-opacity p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-amber-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_be17761e-04ff-4edf-8e1f-841eccc40e55/artifacts/she6yj1e_IMG_6151.png" 
                alt="Charming Creations Logo" 
                className="w-12 h-12 object-contain"
              />
              <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-amber-600 to-teal-600 bg-clip-text text-transparent">
                Charming Creations
              </h1>
            </div>
            
            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 text-gray-700 hover:text-amber-600 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              {/* Jewellery Dropdown */}
              <div className="relative group">
                <Link to="/jewellery" className="flex items-center text-gray-700 hover:text-amber-600 transition-colors font-medium">
                  Jewellery
                  <ChevronDown className="w-4 h-4 ml-1" />
                </Link>
                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <Link to="/brooch" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Brooch</Link>
                  <Link to="/earrings" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Earrings</Link>
                  <Link to="/pendants" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Pendants</Link>
                  <Link to="/bracelets" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Bracelets</Link>
                  <Link to="/rings" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Rings</Link>
                  <Link to="/hair-clips" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Hair clips</Link>
                  <Link to="/pendant-set" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Pendant & Earring Set</Link>
                  <Link to="/keycharm-pendant-set" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Keycharm & Pendant Set</Link>
                </div>
              </div>
              
              {/* Homewares Dropdown */}
              <div className="relative group">
                <Link to="/homewares" className="flex items-center text-gray-700 hover:text-amber-600 transition-colors font-medium">
                  Homewares
                  <ChevronDown className="w-4 h-4 ml-1" />
                </Link>
                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 max-h-80 overflow-y-auto">
                  <Link to="/charcuterie-boards" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Charcuterie Boards & Trays</Link>
                  <Link to="/coaster-set" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Coaster Set</Link>
                  <Link to="/fridge-magnet" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Fridge Magnet</Link>
                  <Link to="/paper-weight" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Paper Weight</Link>
                  <Link to="/candle-holder" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Candle Holder</Link>
                  <Link to="/bottle-opener" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Bottle Openers & Stoppers</Link>
                  <Link to="/jars" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Jars</Link>
                  <Link to="/clocks" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Clocks</Link>
                  <Link to="/paw-trinket" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Paw Trinket</Link>
                  <Link to="/ring-holder" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Bunny Ring Holder</Link>
                </div>
              </div>
              
              {/* Animal Collection Dropdown */}
              <div className="relative group">
                <Link to="/animals" className="flex items-center text-gray-700 hover:text-amber-600 transition-colors font-medium">
                  Animals
                  <ChevronDown className="w-4 h-4 ml-1" />
                </Link>
                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 max-h-80 overflow-y-auto">
                  <Link to="/frogs" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Frogs</Link>
                  <Link to="/snails" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Snails</Link>
                  <Link to="/lizards" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Lizards</Link>
                  <Link to="/bunny" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Bunny</Link>
                  <Link to="/octopus" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Octopus</Link>
                  <Link to="/turtles" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Turtles</Link>
                  <Link to="/seahorse" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Seahorse</Link>
                  <Link to="/starfish" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Starfish</Link>
                  <Link to="/birds" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Birds</Link>
                  <Link to="/bears" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Bears</Link>
                  <Link to="/unicorn" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Unicorn</Link>
                  <Link to="/cat" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Cat</Link>
                  <Link to="/hippo" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Hippo</Link>
                </div>
              </div>
              
              {/* Gift Collections Dropdown */}
              <div className="relative group">
                <Link to="/gifts" className="flex items-center text-gray-700 hover:text-amber-600 transition-colors font-medium">
                  Gifts
                  <ChevronDown className="w-4 h-4 ml-1" />
                </Link>
                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <Link to="/pocket-bear-hug" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Pocket Bear Hugs</Link>
                  <Link to="/keycharms" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Key Charms</Link>
                  <Link to="/scull" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Scull</Link>
                  <Link to="/beaded-pens" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Beaded Pens</Link>
                  <Link to="/bundle-deals" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Bundle Deals</Link>
                </div>
              </div>
              
              {/* Celebrations Dropdown */}
              <div className="relative group">
                <Link to="/celebrations" className="flex items-center text-gray-700 hover:text-amber-600 transition-colors font-medium">
                  Celebrations
                  <ChevronDown className="w-4 h-4 ml-1" />
                </Link>
                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <Link to="/christmas" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Christmas</Link>
                  <Link to="/valentines-day" className="block px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors">Valentine's Day</Link>
                </div>
              </div>
              
              <Link to="/search" className="text-gray-700 hover:text-amber-600 transition-colors font-medium">Catalogue</Link>
              <a href="#contact" onClick={(e) => handleAnchorClick(e, '#contact')} className="text-gray-700 hover:text-amber-600 transition-colors font-medium">Contact</a>
              
              <button
                onClick={() => setSearchOpen(true)}
                className="p-2 text-gray-700 hover:text-amber-600 transition-colors"
                aria-label="Search products"
              >
                <Search className="w-5 h-5" />
              </button>
            </nav>
          </div>
          
          {/* Mobile Navigation Menu */}
          {mobileMenuOpen && (
            <nav className="md:hidden border-t border-amber-200 mt-4 pt-4 pb-2 animate-in slide-in-from-top duration-200 max-h-[70vh] overflow-y-auto">
              <div className="flex flex-col space-y-2">
                {/* Jewellery */}
                <div className="border-b border-gray-100 pb-3">
                  <Link to="/jewellery" className="block text-amber-600 font-semibold py-2 px-2" onClick={() => setMobileMenuOpen(false)}>Jewellery</Link>
                  <div className="pl-4 space-y-1">
                    <Link to="/brooch" className="block text-gray-600 py-1 px-2" onClick={() => setMobileMenuOpen(false)}>Brooch</Link>
                    <Link to="/earrings" className="block text-gray-600 py-1 px-2" onClick={() => setMobileMenuOpen(false)}>Earrings</Link>
                    <Link to="/pendants" className="block text-gray-600 py-1 px-2" onClick={() => setMobileMenuOpen(false)}>Pendants</Link>
                    <Link to="/bracelets" className="block text-gray-600 py-1 px-2" onClick={() => setMobileMenuOpen(false)}>Bracelets</Link>
                    <Link to="/rings" className="block text-gray-600 py-1 px-2" onClick={() => setMobileMenuOpen(false)}>Rings</Link>
                    <Link to="/hair-clips" className="block text-gray-600 py-1 px-2" onClick={() => setMobileMenuOpen(false)}>Hair clips</Link>
                  </div>
                </div>
                
                {/* Homewares */}
                <div className="border-b border-gray-100 pb-3">
                  <Link to="/homewares" className="block text-amber-600 font-semibold py-2 px-2" onClick={() => setMobileMenuOpen(false)}>Homewares</Link>
                  <div className="pl-4 space-y-1">
                    <Link to="/charcuterie-boards" className="block text-gray-600 py-1 px-2" onClick={() => setMobileMenuOpen(false)}>Charcuterie Boards</Link>
                    <Link to="/coaster-set" className="block text-gray-600 py-1 px-2" onClick={() => setMobileMenuOpen(false)}>Coaster Set</Link>
                    <Link to="/clocks" className="block text-gray-600 py-1 px-2" onClick={() => setMobileMenuOpen(false)}>Clocks</Link>
                    <Link to="/ring-holder" className="block text-gray-600 py-1 px-2" onClick={() => setMobileMenuOpen(false)}>Bunny Ring Holder</Link>
                  </div>
                </div>
                
                {/* Animal Collection */}
                <div className="border-b border-gray-100 pb-3">
                  <Link to="/animals" className="block text-amber-600 font-semibold py-2 px-2" onClick={() => setMobileMenuOpen(false)}>Animal Collection</Link>
                  <div className="pl-4 space-y-1">
                    <Link to="/frogs" className="block text-gray-600 py-1 px-2" onClick={() => setMobileMenuOpen(false)}>Frogs</Link>
                    <Link to="/bunny" className="block text-gray-600 py-1 px-2" onClick={() => setMobileMenuOpen(false)}>Bunny</Link>
                    <Link to="/turtles" className="block text-gray-600 py-1 px-2" onClick={() => setMobileMenuOpen(false)}>Turtles</Link>
                    <Link to="/bears" className="block text-gray-600 py-1 px-2" onClick={() => setMobileMenuOpen(false)}>Bears</Link>
                  </div>
                </div>
                
                {/* Gift Collections */}
                <div className="border-b border-gray-100 pb-3">
                  <Link to="/gifts" className="block text-amber-600 font-semibold py-2 px-2" onClick={() => setMobileMenuOpen(false)}>Gift Collections</Link>
                  <div className="pl-4 space-y-1">
                    <Link to="/pocket-bear-hug" className="block text-gray-600 py-1 px-2" onClick={() => setMobileMenuOpen(false)}>Pocket Bear Hugs</Link>
                    <Link to="/keycharms" className="block text-gray-600 py-1 px-2" onClick={() => setMobileMenuOpen(false)}>Key Charms</Link>
                    <Link to="/beaded-pens" className="block text-gray-600 py-1 px-2" onClick={() => setMobileMenuOpen(false)}>Beaded Pens</Link>
                    <Link to="/bundle-deals" className="block text-gray-600 py-1 px-2" onClick={() => setMobileMenuOpen(false)}>Bundle Deals</Link>
                  </div>
                </div>
                
                {/* Celebrations */}
                <div className="border-b border-gray-100 pb-3">
                  <Link to="/celebrations" className="block text-amber-600 font-semibold py-2 px-2" onClick={() => setMobileMenuOpen(false)}>Celebrations</Link>
                  <div className="pl-4 space-y-1">
                    <Link to="/christmas" className="block text-gray-600 py-1 px-2" onClick={() => setMobileMenuOpen(false)}>Christmas</Link>
                    <Link to="/valentines-day" className="block text-gray-600 py-1 px-2" onClick={() => setMobileMenuOpen(false)}>Valentine's Day</Link>
                  </div>
                </div>
                
                {/* Other Links */}
                <Link 
                  to="/search"
                  className="text-gray-700 hover:text-amber-600 transition-colors font-medium py-2 px-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Catalogue
                </Link>
                <a 
                  href="#contact" 
                  onClick={(e) => handleAnchorClick(e, '#contact')}
                  className="text-gray-700 hover:text-amber-600 transition-colors font-medium py-2 px-2"
                >
                  Contact
                </a>
                <button
                  onClick={() => { setMobileMenuOpen(false); setSearchOpen(true); }}
                  className="inline-flex items-center justify-center border-2 border-amber-500 text-amber-600 px-4 py-3 rounded-md font-medium hover:bg-amber-50 transition-colors mt-2"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Search Products
                </button>
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-100/50 via-transparent to-teal-100/50"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Limited Time Offer Banner */}
            <div className="mb-6 inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-orange-500 text-white px-6 py-2 rounded-full text-sm font-semibold animate-pulse">
              <span>🔥</span>
              <span>FREE SHIPPING Australia-wide | Use code WELCOME10 for 10% OFF</span>
              <span>🔥</span>
            </div>
            
            {/* Social Proof Counter - #2 */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <Badge className="bg-white/90 text-teal-700 hover:bg-white px-4 py-2 text-sm font-semibold border-2 border-teal-200">
                <Users className="w-4 h-4 mr-2" />
                Join 2,000+ Happy Customers
              </Badge>
              <Badge className="bg-white/90 text-amber-700 hover:bg-white px-4 py-2 text-sm font-semibold border-2 border-amber-200">
                <Star className="w-4 h-4 mr-2 fill-amber-500 text-amber-500" />
                4.9/5 Rating
              </Badge>
            </div>
            
            <Badge className="mb-6 bg-amber-100 text-amber-800 hover:bg-amber-200 px-4 py-2 text-sm font-semibold">
              <Sparkles className="w-4 h-4 mr-2" />
              Handcrafted with Love
            </Badge>
            <h2 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-amber-700 via-coral-500 to-teal-600 bg-clip-text text-transparent leading-tight">
              Handcrafted Resin Art
            </h2>
            <p className="text-xl md:text-2xl text-gray-700 mb-8 leading-relaxed">
              Discover unique jewellery, homewares & stationery inspired by nature
            </p>
            {/* Enhanced CTAs - #3 */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link 
                to="/search"
                className="inline-flex items-center justify-center bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-lg hover:shadow-xl transition-all px-8 py-4 text-lg font-medium rounded-md"
              >
                Shop Handcrafted Pieces
                <ChevronRight className="w-5 h-5 ml-2" />
              </Link>
              <Link 
                to="/jewellery"
                className="inline-flex items-center justify-center border-2 border-teal-500 text-teal-700 hover:bg-teal-50 px-8 py-4 text-lg font-medium rounded-md transition-all"
              >
                <Heart className="w-5 h-5 mr-2" />
                Browse Jewellery
              </Link>
            </div>
            
            {/* Trust Indicators Below CTA - #4 */}
            <div className="mt-8 flex items-center justify-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-teal-600" />
                <span>30-Day Returns</span>
              </div>
              <div className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-teal-600" />
                <span>Secure Checkout</span>
              </div>
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-teal-600" />
                <span>Free Shipping Australia</span>
              </div>
            </div>
          </div>
        </div>
        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-amber-200 rounded-full opacity-20 blur-xl"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-teal-200 rounded-full opacity-20 blur-xl"></div>
      </section>

      {/* Product Types Grid */}
      <section className="py-16 bg-white" id="shop">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Gifts They'll Love</h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explore our collection of handcrafted resin art pieces, each designed with passion and creativity
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {productTypes.map((type) => (
              <Link 
                key={type.id}
                to={type.link}
                className="block"
              >
                <Card className="group cursor-pointer hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-amber-300 overflow-hidden">
                  <CardContent className="p-0">
                    <div className="aspect-square overflow-hidden bg-gradient-to-br from-amber-50 to-teal-50">
                      <img 
                        src={type.image} 
                        alt={type.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4 text-center">
                      <h4 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">{type.name}</h4>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Main Categories */}
      <section className="py-20 bg-gradient-to-b from-white to-amber-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Shop by Category</h3>
            <p className="text-lg text-gray-600">Curated collections for every style</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {categories.map((category) => (
              <Link 
                key={category.id}
                to={category.link}
                className="block"
              >
                <Card className="group cursor-pointer hover:shadow-2xl transition-all duration-300 border-0 overflow-hidden bg-white">
                  <CardContent className="p-0">
                    <div className="aspect-square overflow-hidden bg-gradient-to-br from-amber-100 to-teal-100">
                      <img 
                        src={category.image} 
                        alt={category.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div className="p-6">
                      <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-amber-600 transition-colors">{category.name}</h4>
                    <p className="text-gray-600 mb-4 leading-relaxed">{category.description}</p>
                    <span className="text-teal-600 hover:text-teal-700 font-semibold inline-flex items-center">
                      Browse {category.name} <ChevronRight className="w-4 h-4 ml-1" />
                    </span>
                  </div>
                </CardContent>
              </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-white" id="featured">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-coral-100 text-coral-800 px-4 py-2 text-sm font-semibold">
              <Star className="w-4 h-4 mr-2" />
              Best Sellers
            </Badge>
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Featured Earrings</h3>
            <p className="text-lg text-gray-600">Jewellery that completes the outfit</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredProducts.map((product, index) => (
              <Card 
                key={product.id} 
                className="group hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-teal-300 overflow-hidden relative"
              >
                {/* Urgency Badges */}
                {index < 3 && (
                  <div className="absolute top-3 left-3 z-10">
                    <Badge className="bg-red-500 text-white font-bold animate-pulse">
                      🔥 Only {Math.floor(Math.random() * 4) + 2} Left!
                    </Badge>
                  </div>
                )}
                {index >= 3 && index < 5 && (
                  <div className="absolute top-3 left-3 z-10">
                    <Badge className="bg-amber-500 text-white font-bold">
                      ⚡ Trending
                    </Badge>
                  </div>
                )}
                
                {/* Quick View Button */}
                <button
                  onClick={() => setQuickViewProduct({...product, category: 'earrings'})}
                  className="absolute top-3 right-3 z-10 p-2 bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white shadow-md"
                  title="Quick View"
                >
                  <Eye className="w-4 h-4 text-gray-700" />
                </button>
                
                <CardContent className="p-0">
                  <div 
                    onClick={() => setQuickViewProduct({...product, category: 'earrings'})}
                    className="block aspect-square overflow-hidden bg-gradient-to-br from-white to-teal-50 cursor-pointer"
                  >
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500 p-4"
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-teal-600 transition-colors">{product.name}</h4>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-2xl font-bold text-amber-600">${product.price.toFixed(2)}</span>
                      {!product.inStock && (
                        <Badge variant="secondary" className="bg-gray-100 text-gray-600">Sold Out</Badge>
                      )}
                    </div>
                    
                    {/* Buy Now Button */}
                    <a 
                      href={product.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => axios.post(`${API}/analytics/track?event_type=product_click&product_id=${product.id}&product_name=${encodeURIComponent(product.name)}`).catch(() => {})}
                      className="w-full inline-flex items-center justify-center bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-bold py-3 text-base shadow-lg rounded-md"
                    >
                      <ShoppingBag className="w-5 h-5 mr-2" />
                      Buy Now
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link 
              to="/search"
              className="inline-flex items-center justify-center bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white shadow-lg px-8 py-3 text-lg font-medium rounded-md"
            >
              Explore All Unique Pieces
              <ChevronRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-gradient-to-b from-amber-50 to-white" id="about">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <Badge className="mb-4 bg-teal-100 text-teal-800 px-4 py-2 text-sm font-semibold">
                  <Heart className="w-4 h-4 mr-2" />
                  Our Story
                </Badge>
                <h3 className="text-4xl font-bold text-gray-900 mb-6">Passion & Dedication</h3>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Charming Creations is passionate and dedicated to creating exceptional resin art pieces that inspire and captivate.
                </p>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Each artwork is meticulously handcrafted, drawing inspiration from nature and evoking emotions. Our pieces blend beauty with functionality, making them perfect for everyday wear or special occasions.
                </p>
                <Button className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-md">
                  Learn More About Us
                </Button>
              </div>
              <div className="relative">
                <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://cdn.shopify.com/s/files/1/0746/9442/6899/files/IMG_8471.jpg?v=1767001687" 
                    alt="Charming Creations Art"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-br from-amber-400 to-teal-400 rounded-full opacity-20 blur-2xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">What Our Customers Say</h3>
            <p className="text-lg text-gray-600">Don't just take our word for it</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2 border-amber-100 hover:border-amber-300 transition-all hover:shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-amber-500 fill-amber-500" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 leading-relaxed italic">"{testimonial.text}"</p>
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-teal-400 flex items-center justify-center text-white font-semibold mr-3">
                      {testimonial.avatar}
                    </div>
                    <span className="font-semibold text-gray-900">{testimonial.name}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-white" id="contact">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <MessageCircle className="w-16 h-16 mx-auto mb-6 text-teal-600" />
              <h3 className="text-4xl font-bold text-gray-900 mb-4">Get in Touch</h3>
              <p className="text-xl text-gray-700">
                Have a question or custom order request? We'd love to hear from you!
              </p>
            </div>
            
            {!contactSubmitted ? (
              <Card className="border-2 border-teal-200">
                <CardContent className="p-8">
                  <form onSubmit={handleContactSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                        <Input 
                          type="text"
                          placeholder="Your name"
                          value={contactForm.name}
                          onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                          className="border-2 border-gray-300 focus:border-teal-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                        <Input 
                          type="email"
                          placeholder="your@email.com"
                          value={contactForm.email}
                          onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                          className="border-2 border-gray-300 focus:border-teal-500"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Subject</label>
                      <Input 
                        type="text"
                        placeholder="What is this about?"
                        value={contactForm.subject}
                        onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                        className="border-2 border-gray-300 focus:border-teal-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Message</label>
                      <Textarea 
                        placeholder="Tell us more..."
                        value={contactForm.message}
                        onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                        className="border-2 border-gray-300 focus:border-teal-500 min-h-[150px]"
                        required
                      />
                    </div>
                    {contactError && (
                      <div className="bg-red-50 text-red-600 p-4 rounded-lg text-sm">
                        {contactError}
                      </div>
                    )}
                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white shadow-lg"
                      disabled={contactSubmitting}
                    >
                      {contactSubmitting ? 'Sending...' : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-2 border-teal-300 bg-teal-50">
                <CardContent className="p-8 text-center">
                  <div className="flex items-center justify-center text-teal-600 text-xl font-semibold mb-2">
                    <Heart className="w-6 h-6 mr-2 fill-teal-600" />
                    Thank you for reaching out!
                  </div>
                  <p className="text-gray-700">We'll get back to you as soon as possible.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-gradient-to-br from-amber-100 via-coral-50 to-teal-100">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <Mail className="w-16 h-16 mx-auto mb-6 text-amber-600" />
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Join Our Community</h3>
            <p className="text-xl text-gray-700 mb-2">
              Get 10% OFF NOW + exclusive updates on new collections
            </p>
            <p className="text-sm text-gray-600 mb-8">Limited time offer for new subscribers!</p>
            {!subscribed ? (
              <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <Input 
                  type="email" 
                  placeholder="Enter your email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 px-6 py-6 text-lg border-2 border-amber-300 focus:border-amber-500 rounded-lg"
                  required
                  disabled={subscribing}
                />
                <Button 
                  type="submit" 
                  size="lg" 
                  className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-lg px-8 py-6 text-lg whitespace-nowrap"
                  disabled={subscribing}
                >
                  {subscribing ? 'Subscribing...' : 'Get My 10% Off'}
                </Button>
              </form>
            ) : (
              <div className="bg-white rounded-lg p-6 shadow-lg max-w-md mx-auto">
                <div className="flex items-center justify-center text-teal-600 text-lg font-semibold">
                  <Heart className="w-6 h-6 mr-2 fill-teal-600" />
                  Thank you for subscribing!
                </div>
              </div>
            )}
            {subscribeError && !subscribed && (
              <div className="mt-4 bg-red-50 text-red-600 p-4 rounded-lg max-w-md mx-auto">
                {subscribeError}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Recently Viewed Products */}
      <RecentlyViewed onProductClick={(product) => setQuickViewProduct(product)} />

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          {/* Trust Badges - #4 */}
          <div className="mb-12 pb-8 border-b border-gray-800">
            <div className="grid md:grid-cols-4 gap-8 text-center">
              <div className="flex flex-col items-center">
                <Shield className="w-12 h-12 text-teal-400 mb-3" />
                <h5 className="font-semibold mb-2">Secure Payment</h5>
                <p className="text-sm text-gray-400">SSL Encrypted Checkout</p>
              </div>
              <div className="flex flex-col items-center">
                <Package className="w-12 h-12 text-teal-400 mb-3" />
                <h5 className="font-semibold mb-2">Free Shipping</h5>
                <p className="text-sm text-gray-400">All Australian Orders</p>
              </div>
              <div className="flex flex-col items-center">
                <Heart className="w-12 h-12 text-teal-400 mb-3" />
                <h5 className="font-semibold mb-2">30-Day Returns</h5>
                <p className="text-sm text-gray-400">Money-Back Guarantee</p>
              </div>
              <div className="flex flex-col items-center">
                <Sparkles className="w-12 h-12 text-teal-400 mb-3" />
                <h5 className="font-semibold mb-2">Handcrafted Quality</h5>
                <p className="text-sm text-gray-400">Unique & Individual</p>
              </div>
            </div>
            
            {/* Payment Icons */}
            <div className="mt-8 flex items-center justify-center gap-4">
              <span className="text-sm text-gray-400">We Accept:</span>
              <div className="flex items-center gap-3">
                <CreditCard className="w-10 h-6 text-gray-400" />
                <div className="bg-white rounded px-3 py-1">
                  <span className="text-blue-600 font-bold text-sm">VISA</span>
                </div>
                <div className="bg-white rounded px-3 py-1">
                  <span className="text-orange-600 font-bold text-sm">MC</span>
                </div>
                <div className="bg-white rounded px-3 py-1">
                  <span className="text-blue-800 font-bold text-sm">AMEX</span>
                </div>
                <Lock className="w-5 h-5 text-teal-400 ml-2" />
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_be17761e-04ff-4edf-8e1f-841eccc40e55/artifacts/she6yj1e_IMG_6151.png" 
                  alt="Charming Creations Logo" 
                  className="w-10 h-10 object-contain"
                />
                <h4 className="text-xl font-bold">Charming Creations</h4>
              </div>
              <p className="text-gray-400 leading-relaxed">
                Handcrafted resin art that inspires and captivates, drawing inspiration from nature.
              </p>
            </div>
            <div>
              <h5 className="font-semibold text-lg mb-4">Shop</h5>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/jewellery" className="hover:text-amber-400 transition-colors">Jewellery</Link></li>
                <li><Link to="/homewares" className="hover:text-amber-400 transition-colors">Homewares</Link></li>
                <li><Link to="/beaded-pens" className="hover:text-amber-400 transition-colors">Beaded Pens</Link></li>
                <li><Link to="/search" className="hover:text-amber-400 transition-colors">All Products</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-lg mb-4">Company</h5>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#about" className="hover:text-amber-400 transition-colors">About Us</a></li>
                <li><a href="#contact" className="hover:text-amber-400 transition-colors">Contact</a></li>
                <li><Link to="/shipping" className="hover:text-amber-400 transition-colors">Shipping Info</Link></li>
                <li><Link to="/returns" className="hover:text-amber-400 transition-colors">Returns</Link></li>
                <li><Link to="/track-order" className="hover:text-amber-400 transition-colors">Track Order</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-lg mb-4">Follow Us</h5>
              <div className="flex space-x-3">
                <a href="https://www.facebook.com/share/1FehrmyNW6/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href="https://www.instagram.com/charming_creations__gifts?igsh=cmgzdHcxODJ3dHlz&utm_source=qr" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-pink-600 transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="https://youtube.com/@charmingcreationsandgifts?si=Xc8AoFy2zRM1BOTf" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors">
                  <Youtube className="w-5 h-5" />
                </a>
                <a href="https://www.tiktok.com/@charmingcreationsgifts" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-black transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                  </svg>
                </a>
                <a href="https://www.pinterest.com.au/charmingcreationsgifts" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-700 transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0a12 12 0 00-4.37 23.17c-.1-.88-.19-2.24 0-3.2l1.45-6.14s-.37-.74-.37-1.83c0-1.72 1-3 2.24-3 1.05 0 1.56.79 1.56 1.74 0 1.06-.68 2.64-1 4.1-.29 1.21.61 2.2 1.8 2.2 2.16 0 3.83-2.28 3.83-5.57 0-2.91-2.09-4.95-5.08-4.95-3.46 0-5.49 2.6-5.49 5.28 0 1.05.4 2.17.9 2.78a.3.3 0 01.07.29l-.33 1.36c-.05.22-.18.27-.42.16-1.52-.71-2.47-2.93-2.47-4.72 0-3.83 2.78-7.35 8-7.35 4.2 0 7.47 3 7.47 7s-2.65 7.14-6.32 7.14a3.27 3.27 0 01-2.8-1.4l-.76 2.9a12.81 12.81 0 01-1.46 3.08A12 12 0 1012 0z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2025 Charming Creations. All rights reserved. Handcrafted with love.</p>
          </div>
        </div>
      </footer>

      {/* Product Search Modal */}
      <ProductSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      
      {/* Quick View Modal */}
      <QuickViewModal 
        product={quickViewProduct} 
        isOpen={!!quickViewProduct} 
        onClose={() => setQuickViewProduct(null)} 
      />
      
      {/* Sticky Shop Now Bar */}
      <StickyShopBar show={showStickyBar} />
      
      {/* Social Proof Notifications */}
      <SocialProofNotification />
    </div>
  );
};

export default Home;